import type { ProjectDataScope } from '../projects.types';
import type { LinkedContextItem } from './linked-context';

/** Frozen Work-composer context: a named patient, or the host session. */
export type ContextLock = {
  kind: 'patient' | 'session';
  label: string | null;
};

/** True when the project was frozen to a patient or session at create. */
export function isRestrictedProjectDataScope(
  scope: ProjectDataScope | null | undefined
): boolean {
  return scope?.kind === 'patient' || scope?.kind === 'session';
}

/** Patient display name for the locked composer chip, if the host has one. */
export function lockedContextPatientName(
  items: readonly LinkedContextItem[]
): string | null {
  const item = items[0];
  if (!item) return null;
  if (item.type === 'patient') return item.patientName.trim() || null;
  if (item.type === 'session') return item.patientName?.trim() || null;
  return null;
}

/** Human name from a patient-profile payload (ids-only detail is not enough). */
export function patientProfileDisplayName(
  profile:
    | {
        first_name?: string | null;
        last_name?: string | null;
        demographic_string?: string | null;
      }
    | null
    | undefined
): string | null {
  if (!profile) return null;
  const full = [profile.first_name, profile.last_name].filter(Boolean).join(' ').trim();
  if (full) return full;
  return profile.demographic_string?.trim() || null;
}

/**
 * Chip label inputs: linkedItems (session panel seed) first, then a fetched
 * profile name when ``data_scope.kind`` is patient and the composer has no chip.
 */
export function resolveLockedComposerPatientName(args: {
  linkedItems: readonly LinkedContextItem[];
  dataScope?: ProjectDataScope | null;
  fetchedPatientName?: string | null;
}): { patientName: string | null; patientScoped: boolean } {
  const patientScoped = args.dataScope?.kind === 'patient';
  const fromItems = lockedContextPatientName(args.linkedItems);
  const fetched = args.fetchedPatientName?.trim() || null;
  return {
    patientName: fromItems ?? (patientScoped ? fetched : null),
    patientScoped,
  };
}

/** Single lock for the physical context card. Unrestricted → null. */
export function resolveContextLock(args: {
  linkedItems: readonly LinkedContextItem[];
  dataScope?: ProjectDataScope | null;
  fetchedPatientName?: string | null;
  restricted: boolean;
}): ContextLock | null {
  if (!args.restricted) return null;
  const { patientName, patientScoped } = resolveLockedComposerPatientName(args);
  if (patientName) return { kind: 'patient', label: patientName };
  if (patientScoped) return { kind: 'patient', label: null };
  return { kind: 'session', label: null };
}

/** Profile id to fetch when a patient-scoped lock has no name on linkedItems. */
export function lockedComposerPatientFetchId(args: {
  enabled: boolean;
  linkedItems: readonly LinkedContextItem[];
  dataScope: ProjectDataScope | null | undefined;
}): string | undefined {
  if (!args.enabled) return undefined;
  const resolved = resolveLockedComposerPatientName({
    linkedItems: args.linkedItems,
    dataScope: args.dataScope,
  });
  if (!resolved.patientScoped || resolved.patientName) return undefined;
  return args.dataScope?.patient_profile_id?.trim() || undefined;
}
